import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { getApplications, Application } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useSidebar } from '../context/SidebarContext';
import { useTheme } from '../context/ThemeContext';
import ConversationSidebar from '../components/ConversationSidebar';
import Header from '../components/Header';

const ApplicationsPage: React.FC = () => {
  const [applications, setApplications] = useState<Application[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { getAccessToken } = useAuth();
  const { isSidebarOpen, toggleSidebar } = useSidebar();
  const { colors, theme } = useTheme();
  const history = useHistory();
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const fetchApplications = useCallback(async (search?: string) => {
    try {
      setIsLoading(true);
      await getAccessToken();
      const params: { search?: string } = {};
      if (search) params.search = search;
      const apps = await getApplications(Object.keys(params).length > 0 ? params : undefined);
      setApplications(apps);
    } catch (err) {
      console.error('Error loading applications:', err);
      setError('Failed to load applications');
    } finally {
      setIsLoading(false);
    }
  }, [getAccessToken]);

  useEffect(() => {
    fetchApplications();
  }, []);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      fetchApplications(value);
    }, 600);
  };

  const handleSelectConversation = (conversationId: string) => {
    if (conversationId === '') {
      history.push('/');
    } else {
      history.push('/', { conversationId });
    }
  };

  const handleAppClick = (app: Application) => {
    history.push('/', {
      applicationData: {
        id: app.id,
        title: app.title,
        customPrompt: app.custom_prompt,
        defaultModelProvider: app.default_model_provider,
        defaultModel: app.default_model,
        defaultModelApiVersion: app.default_model_api_version
      }
    });
  };

  const AppCard: React.FC<{ app: Application }> = ({ app }) => (
    <div
      style={{
        backgroundColor: colors.surface,
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: theme === 'light' ? '0 2px 8px rgba(0, 0, 0, 0.06)' : '0 2px 8px rgba(0, 0, 0, 0.25)',
        border: `1px solid ${colors.border}`,
        cursor: app.can_use ? 'pointer' : 'default',
        transition: 'all 0.2s ease',
        display: 'flex',
        flexDirection: 'column',
        opacity: app.can_use ? 1 : 0.45,
      }}
      onMouseEnter={(e) => {
        if (!app.can_use) return;
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = theme === 'light' ? '0 12px 32px rgba(0, 0, 0, 0.12)' : '0 12px 32px rgba(0, 0, 0, 0.4)';
        e.currentTarget.style.borderColor = colors.borderHover;
      }}
      onMouseLeave={(e) => {
        if (!app.can_use) return;
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = theme === 'light' ? '0 2px 8px rgba(0, 0, 0, 0.06)' : '0 2px 8px rgba(0, 0, 0, 0.25)';
        e.currentTarget.style.borderColor = colors.border;
      }}
      onClick={() => app.can_use && handleAppClick(app)}
    >
      {/* Visual hero area */}
      <div style={{
        background: theme === 'light'
          ? 'linear-gradient(135deg, #f0f4ff 0%, #e8eeff 50%, #f5f0ff 100%)'
          : 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 50%, rgba(102, 126, 234, 0.1) 100%)',
        padding: '32px 24px',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
      }}>
        <div style={{
          width: '64px',
          height: '64px',
          borderRadius: '16px',
          overflow: 'hidden',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: colors.surfaceElevated,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        }}>
          <img
            src={`data:image/png;base64,${app.image_base64}`}
            alt={`${app.title} icon`}
            style={{
              width: '40px',
              height: '40px',
              objectFit: 'contain'
            }}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              const fallback = document.createElement('div');
              fallback.style.cssText = `
                width: 40px; height: 40px;
                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                border-radius: 8px;
                display: flex; align-items: center; justify-content: center;
                color: white; font-weight: 600; font-size: 18px;
              `;
              fallback.textContent = app.title.charAt(0).toUpperCase();
              target.parentNode?.appendChild(fallback);
            }}
          />
        </div>
        <h3 style={{
          fontSize: '18px',
          fontWeight: '700',
          color: colors.textPrimary,
          lineHeight: '1.3',
          margin: 0,
        }}>
          {app.title}
        </h3>
      </div>

      {/* Content area */}
      <div style={{ padding: '20px 24px 24px', display: 'flex', flexDirection: 'column', gap: '16px', flex: 1 }}>
        <p style={{
          fontSize: '13px',
          color: colors.textMuted,
          lineHeight: '1.65',
          margin: 0,
        }}>
          {app.description}
        </p>

        {/* Metadata */}
        {(app.typical_audience || app.access) && (
          <div style={{
            display: 'flex',
            gap: '16px',
            borderTop: `1px solid ${colors.border}`,
            paddingTop: '16px',
            marginTop: 'auto',
          }}>
            {app.typical_audience && (
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: '10px',
                  fontWeight: '700',
                  color: colors.textMuted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  marginBottom: '4px',
                }}>
                  Audience
                </div>
                <div style={{
                  fontSize: '12px',
                  color: colors.textSecondary || colors.textMuted,
                  lineHeight: '1.5',
                }}>
                  {app.typical_audience}
                </div>
              </div>
            )}
            {app.access && (
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: '10px',
                  fontWeight: '700',
                  color: colors.textMuted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  marginBottom: '4px',
                }}>
                  Access
                </div>
                <div style={{
                  fontSize: '12px',
                  color: colors.textSecondary || colors.textMuted,
                  lineHeight: '1.5',
                }}>
                  {app.access}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <ConversationSidebar
        isOpen={isSidebarOpen}
        onToggle={toggleSidebar}
        onSelectConversation={handleSelectConversation}
        currentConversationId={undefined}
        refreshTrigger={0}
      />

      <div style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: colors.background,
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        overflow: 'hidden',
        marginLeft: isSidebarOpen ? '320px' : '60px',
        transition: 'margin-left 0.3s ease-in-out'
      }}>
        <Header hideTitle />

        <div style={{
          flex: 1,
          overflow: 'auto',
          padding: '80px 40px 40px 40px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}>

          {isLoading && applications.length === 0 ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '50vh',
            }}>
              <div style={{ color: colors.textMuted, fontSize: '16px' }}>Loading applications...</div>
            </div>
          ) : error ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '50vh',
            }}>
              <div style={{ color: colors.errorHover, fontSize: '16px' }}>{error}</div>
            </div>
          ) : (
            <div style={{ maxWidth: '1200px', width: '100%' }}>
              {/* Header */}
              <div style={{
                textAlign: 'center',
                marginBottom: '48px',
              }}>
                <h1 style={{
                  fontSize: '48px',
                  fontWeight: '700',
                  color: colors.textPrimary,
                  marginBottom: '16px',
                  letterSpacing: '-0.025em'
                }}>
                  Applications
                </h1>
                <p style={{
                  fontSize: '18px',
                  color: colors.textMuted,
                  lineHeight: '1.6',
                  margin: 0,
                  maxWidth: '600px',
                  marginLeft: 'auto',
                  marginRight: 'auto'
                }}>
                  Discover custom versions of Chat VNT 2.0 that combine instructions, extra knowledge, and any combination of skills.
                </p>
              </div>

              {/* Search */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '36px',
              }}>
                <div style={{ position: 'relative', width: '340px', flexShrink: 0 }}>
                  <svg
                    width="18" height="18" viewBox="0 0 24 24" fill="none"
                    stroke={colors.textMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)' }}
                  >
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.35-4.35" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search applications..."
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 16px 10px 44px',
                      fontSize: '15px',
                      border: `1px solid ${colors.border}`,
                      borderRadius: '12px',
                      backgroundColor: colors.surface,
                      color: colors.textPrimary,
                      outline: 'none',
                      boxSizing: 'border-box',
                      transition: 'border-color 0.2s ease',
                    }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = colors.borderHover; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = colors.border; }}
                  />
                </div>
              </div>

              {/* Applications Grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '20px',
              }}>
                {applications
                  .sort((a, b) => a.sort_order - b.sort_order)
                  .map((app) => (
                    <AppCard key={app.id} app={app} />
                  ))}
              </div>

              {applications.length === 0 && !isLoading && (
                <div style={{
                  textAlign: 'center',
                  padding: '60px 20px',
                  color: colors.textMuted
                }}>
                  {searchQuery
                    ? 'No applications match your search.'
                    : 'No applications available at the moment.'}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ApplicationsPage;